import type { ApexOptions } from 'apexcharts';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { ComponentType, Suspense, lazy, useCallback, useMemo } from 'react';
import type { Props as ReactApexChartProps } from 'react-apexcharts';
import { ApexChartSeries } from '../types';
import { formatNumbers } from '../utils';
import { ChartBarData } from './ChartBarWithPositiveNegative';
import { Spinner } from './Spinner';

const Chart = lazy(() =>
  import('react-apexcharts').then(module => ({
    default: ((module as any).default?.default ?? (module as any).default ?? module) as any,
  })),
) as unknown as ComponentType<ReactApexChartProps>;

dayjs.extend(utc);

export interface ChartMultiBarData {
  name: string;
  data: ChartBarData[];
}

export interface ChartMultiBarProps {
  items: ChartMultiBarData[];
  height?: number | string;
  colors?: string[];
  currency?: string;
}

export const ChartMultiBar = ({
  colors = ['#0369A1', '#0EA5E9', '#10B981', '#F59E0B', '#A855F7', '#ABABAB'],
  height = '100%',
  items,
  currency = '',
}: ChartMultiBarProps) => {
  const fmtPct = useCallback(
    (withCurrency = true) =>
      (v: number) =>
        `${formatNumbers(v)} ${withCurrency ? currency : ''}`,
    [currency],
  );

  const series = useMemo<ApexChartSeries[]>(
    () =>
      items.map(item => ({
        name: item.name,
        data: item.data.map(d => ({
          x: typeof d.date === 'number' ? d.date : dayjs(d.date).valueOf(),
          y: d.value,
          columnWidthOffset: -8,
        })),
      })),
    [items],
  );

  const options = useMemo<ApexOptions>(() => {
    const vals = items.flatMap(item => item.data.map(d => d.value));
    let max = Math.max(...vals, 0);
    max = max + max * 0.1; //Fixed: Error: <path> attribute d: Expected number
    return {
      chart: {
        type: 'bar',
        height,
        toolbar: { show: false },
        stacked: false,
        animations: {
          enabled: true,
          speed: 300,
        },
        selection: { enabled: false },
        states: {
          normal: { filter: { type: 'none', value: 0 } },
          hover: { filter: { type: 'none', value: 0 } },
          active: { filter: { type: 'none', value: 0 } },
        },
        zoom: {
          enabled: false,
        },
      },

      legend: {
        position: 'top',
        horizontalAlign: 'left',
        markers: {
          width: 8,
          height: 8,
          offsetX: -2,
        },
        itemMargin: {
          horizontal: 12,
        },
      },

      colors,
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '24px',
          borderRadius: 3,
          distributed: false, //important made not different colors
        },
      },

      dataLabels: {
        enabled: false,
      },
      xaxis: {
        type: 'datetime',

        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: {
          format: 'MMM yy',
          datetimeUTC: true,
          showDuplicates: false,
          rotate: -90,
          rotateAlways: true,
          hideOverlappingLabels: false,
          trim: false,
          minHeight: 72,
          style: {
            colors: '#A3A3A3',
            fontFamily: 'Inter',
            fontWeight: 400,
            fontSize: '12px',
            cssClass: '!font-normal',
          },
        },
      },
      yaxis: {
        min: 0,
        max,
        tickAmount: 4,
        labels: {
          formatter: (val: number) => fmtPct(false)(val),
          style: {
            colors: '#A3A3A3',
            fontFamily: 'Inter',
            fontWeight: 400,
            fontSize: '12px',
          },
        },
      },

      grid: {
        borderColor: 'rgba(0,0,0,0.08)',
        yaxis: { lines: { show: true } },
        xaxis: { lines: { show: false } },
      },

      tooltip: {
        shared: false,
        intersect: true,
        y: {
          formatter: (val: number) => fmtPct(true)(val),
        },
        x: {
          formatter: (val: number) => dayjs(val).format('MMM D, YYYY'),
        },
      },
    };
  }, [items, height, colors, fmtPct]);

  return (
    <Suspense
      fallback={
        <div className="min-h-[250px] h-full w-full flex justify-center items-center">
          <Spinner size="lg" />
        </div>
      }
    >
      <Chart type="bar" options={options} series={series} height={height} />
    </Suspense>
  );
};
